import { useRef, useEffect, useCallback } from 'react';
import axios, { Cancel, CancelToken, CancelTokenSource, isCancel } from 'axios';

/**
 * When a component unmounts, we need to cancel any potentially
 * ongoing Axios calls that result in a state update on success / fail.
 * This function sets up the appropriate useEffect to handle the canceling.
 *
 * @returns {newCancelToken: function, isCancel: function}
 * newCancelToken - used to generate the cancel token sent in the Axios request.
 * isCancel - used to check if error returned in response is a cancel token error.
 * @example
 *  const { newCancelToken, isCancel } = useCancelToken();
 *  useEffect(() => {
 *   axios.get('/important_data', {
 *    cancelToken: newCancelToken()
 * }).then((response) => {
 *  setState(response.data);
 * }).catch((error) => {
 *  if (isCancel(error)) return;
 * });
 * }, [newCancelToken, isCancel]);
 */
export const useCancelToken = (): UseCancelToken => {
  const axiosSource = useRef<CancelTokenSource | null>(null);
  const newCancelToken = useCallback(() => {
    axiosSource.current = axios.CancelToken.source();
    return axiosSource.current.token;
  }, []);
  useEffect(
    () => () => {
      if (axiosSource.current) axiosSource.current.cancel();
    },
    []
  );
  return { newCancelToken, isCancel };
};
type UseCancelToken = {
  newCancelToken: () => CancelToken;
  isCancel: (value: any) => value is Cancel;
};
